import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";

const REASONS = [
  {
    n: "01",
    title: "Military-precision execution",
    body:
      "We were founded by a US Army veteran. Every technician arrives in uniform, on the exact fifteen-minute window we quoted, with a laminated scope-of-work in hand. Nothing on your property is left to interpretation.",
  },
  {
    n: "02",
    title: "One vendor for the entire exterior",
    body:
      "Eleven disciplines under a single Certificate of Insurance. Windows, sidewalks, awnings, parking lot, snow, graffiti, and permanent lighting. You stop juggling three vendors and one clipboard.",
  },
  {
    n: "03",
    title: "Documented, photographed, warranted",
    body:
      "Every service ends with a time-stamped photo report emailed to your inbox before we leave the block. If it's not visible on the report, it's not billed.",
  },
  {
    n: "04",
    title: "The last vendor you'll audition",
    body:
      "Our smallest retainer contracts run four years without turnover. Property managers stop shopping when the standard is set and held.",
  },
];

const COMPARE = [
  {
    dim: "Arrival & appearance",
    typical: "Unmarked truck. T-shirts. Whoever showed up that morning.",
    bespoke: "Uniformed crew, matte-black vehicle wraps, laminated ID.",
  },
  {
    dim: "Scope clarity",
    typical: "Verbal scope on the sidewalk, priced by 'looks like a day'.",
    bespoke: "Written scope, unit-priced by square footage / linear feet.",
  },
  {
    dim: "Water quality",
    typical: "Municipal tap water — mineral spotting within a week.",
    bespoke: "Deionised water, 0 TDS, spot-free for weeks after service.",
  },
  {
    dim: "Insurance",
    typical: "$1M general liability, no umbrella, no auto rider.",
    bespoke: "$2M general liability + $1M umbrella + auto + workers comp.",
  },
  {
    dim: "Documentation",
    typical: "'Trust us, we cleaned it.' Maybe a phone photo.",
    bespoke: "Digital service report, geotagged photos, before/after archive.",
  },
  {
    dim: "Response time",
    typical: "Callback next Tuesday. Maybe.",
    bespoke: "Same-day for retainer clients. 24 hr SLA on any request.",
  },
  {
    dim: "Warranty",
    typical: "Best-effort. No re-service if it rains.",
    bespoke: "24–72 hour weather-event re-clean baked into every retainer.",
  },
  {
    dim: "Turnover",
    typical: "New crew every quarter. Learning your property on your dime.",
    bespoke: "Named crew leads. Smallest contracts run 4+ years unchanged.",
  },
];

const STATS = [
  { value: "11", label: "Exterior disciplines" },
  { value: "$2M", label: "General liability" },
  { value: "24 hr", label: "Quote turnaround" },
  { value: "0", label: "Callbacks last quarter" },
];

export default function WhyBespoke() {
  const { url } = useMedia();

  return (
    <div className="min-h-screen" data-testid="why-bespoke-page">
      <PageHero
        eyebrow="Why Bespoke"
        title={<>The <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>standard,</span><br />held.</>}
        subtitle="Nine out of ten property managers we speak to have already been through two vendors. This is why they stop looking after us."
        image={url("why-hero") || url("pressure-washing")}
        kicker="Written for the property manager who has been burned twice."
      />

      {/* Four reasons block */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">The Four Reasons</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Cheaper is <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>expensive.</span>
              <span className="block mt-2" style={{ color: "var(--text-2)", fontSize: "60%", lineHeight: 1.3 }}>
                Every property manager we serve tried the low bid at least once.
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {REASONS.map((r, i) => (
              <motion.article
                key={r.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="glass-card p-10 relative overflow-hidden group"
                data-testid={`why-reason-${r.n}`}
                data-cursor="hover"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>{r.n}</div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em", lineHeight: 1.15 }}>{r.title}</h3>
                <p className="mt-6 font-body" style={{ color: "var(--text-2)", lineHeight: 1.75 }}>{r.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table — interactive rows */}
      <section className="py-28 md:py-36 relative" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">Side by Side</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Typical cleaner. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Then us.</span>
            </h2>
          </div>

          <div className="rounded-[24px] overflow-hidden" style={{ border: "1px solid var(--line)", background: "rgba(11,11,13,0.6)", backdropFilter: "blur(12px)" }}>
            <div className="grid md:grid-cols-[1.2fr_1.4fr_1.4fr] font-mono text-[10px] uppercase tracking-[0.36em] py-5 px-6 md:px-10" style={{ background: "rgba(198,168,124,0.06)", color: "var(--text-3)", borderBottom: "1px solid var(--line)" }}>
              <div>Dimension</div>
              <div>Typical cleaner</div>
              <div style={{ color: "var(--gold)" }}>Bespoke</div>
            </div>

            {COMPARE.map((c, i) => (
              <motion.div
                key={c.dim}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className="grid md:grid-cols-[1.2fr_1.4fr_1.4fr] gap-4 md:gap-8 py-8 px-6 md:px-10 transition-colors duration-500 hover:bg-[rgba(198,168,124,0.03)]"
                style={{ borderBottom: i < COMPARE.length - 1 ? "1px solid var(--line)" : "none" }}
                data-testid={`compare-row-${i}`}
              >
                <div className="font-display text-lg md:text-xl" style={{ letterSpacing: "-0.01em" }}>{c.dim}</div>
                <div className="flex items-start gap-3" style={{ color: "var(--text-3)" }}>
                  <X size={16} className="shrink-0 mt-1" style={{ color: "#8A2B2E" }} />
                  <span style={{ lineHeight: 1.6 }}>{c.typical}</span>
                </div>
                <div className="flex items-start gap-3" style={{ color: "var(--text)" }}>
                  <Check size={16} className="shrink-0 mt-1" style={{ color: "var(--gold-2)" }} />
                  <span style={{ lineHeight: 1.6 }}>{c.bespoke}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
          {STATS.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.08 }}>
              <div className="font-display leading-none" style={{ fontSize: "clamp(2.6rem, 5vw, 4.5rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>{s.value}</div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--text-3)" }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ letterSpacing: "-0.035em" }}>
            Set the standard on your <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>property.</span>
          </h2>
          <p className="mt-8 font-body text-lg" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
            Twelve seconds to price. Twenty-four hours to service. One vendor for the entire exterior.
          </p>
          <div className="mt-12 flex flex-wrap justify-center items-center gap-5">
            <Link to="/pricing" className="btn-glow" data-testid="why-cta-plans" data-cursor="hover">
              See Instant Plans <ArrowUpRight size={13} />
            </Link>
            <Link to="/audit" className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }} data-testid="why-cta-audit">
              <span className="inline-flex items-center justify-center w-10 h-10" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)" }}>
                <ArrowUpRight size={12} style={{ color: "var(--gold)" }} />
              </span>
              Book a walk-through
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
